import { useState } from 'react';
import { CaretDown, CaretRight, Warning } from '@phosphor-icons/react';
import { useRecentToolCalls, type ToolCallRecord } from '~/hooks/queries/use-inspector';
import { CodeBlock } from '~/components/ui/code';
import { Tip } from '~/components/ui/tip';
import { redactToolInput } from '~/lib/redact-tool-input';

export function ToolCallLog() {
  const { data, isLoading, error } = useRecentToolCalls(25);

  if (isLoading) {
    return <div className="text-xs text-muted-foreground/70 py-1">Loading…</div>;
  }
  if (error) {
    return <div className="text-xs text-destructive py-1">Failed to load tool calls</div>;
  }
  if (!data || data.length === 0) {
    return <div className="text-xs text-muted-foreground/70 py-1">No tool calls yet</div>;
  }

  return (
    <ul className="-mx-3 divide-y divide-border/60">
      {data.map((row) => (
        <ToolCallRow key={row.id} row={row} />
      ))}
    </ul>
  );
}

function ToolCallRow({ row }: { row: ToolCallRecord }) {
  const [open, setOpen] = useState(false);
  const when = formatRelative(row.createdAt);
  const duration = row.durationMs != null ? formatDuration(row.durationMs) : null;

  return (
    <li className="text-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/40 transition-colors"
      >
        {open ? (
          <CaretDown size={11} weight="bold" className="shrink-0 text-muted-foreground" />
        ) : (
          <CaretRight size={11} weight="bold" className="shrink-0 text-muted-foreground" />
        )}
        {row.isError && <Warning size={12} weight="fill" className="shrink-0 text-destructive" />}
        <span className="flex-1 truncate font-mono text-xs">
          {prettyName(row)}
        </span>
        <AutonomyBadge tier={row.autonomyTier} decision={row.autonomyDecision} />
        <span className="text-[length:var(--text-2xs)] tabular-nums text-muted-foreground/70">{when}</span>
      </button>
      {open && (
        <div className="px-3 pb-2 space-y-2">
          <Meta label="Source">
            <span className="font-mono text-xs">{row.server}</span>
            {duration && <span className="text-[length:var(--text-2xs)] text-muted-foreground/70 ml-2">{duration}</span>}
            {row.autonomyTier != null && (
              <span className="text-[length:var(--text-2xs)] text-muted-foreground/70 ml-2">
                tier {row.autonomyTier} · {row.autonomyDecision ?? 'allow'}
              </span>
            )}
          </Meta>
          <Meta label="Args">
            {/* FRONT-2716: same masking as the chat's tool card. */}
            <Json value={redactToolInput(row.toolName, row.args ?? {})} />
          </Meta>
          {row.isError ? (
            <Meta label="Error">
              <pre className="text-xs text-destructive whitespace-pre-wrap break-words">
                {row.errorMessage ?? 'Unknown error'}
              </pre>
            </Meta>
          ) : (
            <Meta label="Result">
              <Json value={row.result} />
            </Meta>
          )}
        </div>
      )}
    </li>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[length:var(--text-2xs)] uppercase tracking-[0.08em] text-muted-foreground/60 font-medium mb-0.5">
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Json({ value }: { value: unknown }) {
  let text: string;
  try {
    text = JSON.stringify(value, null, 2);
  } catch {
    text = String(value);
  }
  return <CodeBlock wrap>{text}</CodeBlock>;
}

function prettyName(row: ToolCallRecord): string {
  // For MCP tools the prefixed name is mcp__<server>__<tool>; for local
  // dispatch it's just the tool name. Either way, show the most distinctive
  // suffix so the row reads like a function call.
  return row.prefixedName || row.toolName;
}

function formatRelative(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const delta = Math.max(0, Date.now() - t);
  if (delta < 60_000) return `${Math.floor(delta / 1000)}s ago`;
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
  return `${Math.floor(delta / 86_400_000)}d ago`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

/**
 * Tiny autonomy chip on each row. Hidden for tier-null (read-only / pre-gate)
 * rows so the strip stays quiet; coloured by decision so an approval-required
 * call pops without us having to read the label.
 */
function AutonomyBadge({ tier, decision }: { tier: number | null; decision: string | null }) {
  if (tier == null) return null;
  const cls =
    decision === 'refused' || decision === 'rejected'
      ? 'bg-destructive/15 text-destructive border-destructive/30'
      : decision === 'approved' || decision === 'auto-approved'
        ? 'bg-success/15 text-success border-success/30'
        : decision === 'inform'
          ? 'bg-chart-2/15 text-chart-2 border-chart-2/30'
          : 'bg-muted text-muted-foreground border-border';
  const label = decision ?? `tier ${tier}`;
  return (
    <Tip label={`Autonomy tier ${tier} · ${decision ?? 'allow'}`}>
      <span
        className={`text-[length:var(--text-2xs)] uppercase tracking-wider px-1 py-0.5 rounded font-medium border shrink-0 ${cls}`}
      >
        {label}
      </span>
    </Tip>
  );
}
